import React, { useCallback, useEffect, useRef } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import {
  AlignItems,
  Body3,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading4,
  Heading5,
  Join,
  JustifyContent,
  Space,
  Icon,
  Text,
  IconVariant,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'
import moment from 'moment'
import { browserHistory } from 'react-router'

import { WhatsComingUpNext } from '../WhatsComingUpNext/WhatsComingUpNext'
import { useWelcomeTracking } from '../hooks/useWelcomeTracking'
import { Carousel } from './Carousel/Carousel'

export type Order = {
  id: string
  deliveryDate: string
  shouldCutoffAt: string
  number: string
  box: {
    numPortions: string
    numRecipes: string
  }
  prices: {
    isDeliveryFree: boolean
    percentageOff: string
  }
}

type Props = {
  orders: Record<string, Order>
  userLoadOrders: (forceRefresh?: boolean) => void
  campaignId?: string
}

const DaysToOrder = withCitrusTheme(Box, (theme) => ({
  borderRadius: '8px',
  width: '100%',
  background: theme.color(Color.Success_50),
  padding: '12px',
  height: '44px',
  fontWeight: FontWeight.Bold,
}))

const IconBackground = withCitrusTheme(Box, (theme) => ({
  background: theme.color(Color.Success_900),
  height: '28px',
  width: '28px',
  display: Display.Flex,
  justifyContent: JustifyContent.Center,
  borderRadius: '14px',
  alignItems: AlignItems.Center,
}))

const WhiteIcon = withCitrusTheme(Icon, (theme) => ({
  color: theme.color(Color.White),
  width: '20px',
  height: '20.83px',
}))

const ChooseHeading4 = withCitrusTheme(Heading4, (theme) => ({
  paddingBottom: '8px',
  color: theme.color(Color.ColdGrey_900),
}))

const getFormattedDeliveryDate = (deliveryDate: string) =>
  moment(deliveryDate, 'YYYY-MM-DD').format('dddd Do MMM')
let ORDERS_FETCH_CURRENT_ATTEMPTS = 0
export const ORDERS_FETCH_MAX_ATTEMPTS = 6
export const WAIT_TIME = 2000

export const BuildYourNextBox = ({ orders, userLoadOrders, campaignId }: Props) => {
  const {
    trackSecondOrderIsLoaded,
    trackClickOnViewUpcomingBoxes,
    trackClickOnChooseRecipes,
    trackNextOrderIsLoadedWithinPollingLimit,
  } = useWelcomeTracking()
  const [hasOrderRetryFailed, setHasOrderRetryFailed] = React.useState<boolean>(false)
  const orderIds = Object.keys(orders)
  const secondaryOrder = orders[orderIds[1]]

  const prevOrderCount = useRef(orderIds.length)
  const pollingAttempts = useRef(ORDERS_FETCH_CURRENT_ATTEMPTS)
  const hasTrackedSecondOrder = useRef(false)

  const tryFetchOrders = useCallback(() => {
    const intervalId = setInterval(() => {
      if (orderIds.length > 1) {
        clearInterval(intervalId)

        return
      }

      if (ORDERS_FETCH_CURRENT_ATTEMPTS <= ORDERS_FETCH_MAX_ATTEMPTS) {
        userLoadOrders(true)
        ORDERS_FETCH_CURRENT_ATTEMPTS += 1
        pollingAttempts.current = ORDERS_FETCH_CURRENT_ATTEMPTS
      } else {
        clearInterval(intervalId)

        datadogLogs.logger.error(
          `Exceeded time to load orders after signing up. Order IDs: ${Object.keys(orders)}`,
        )

        setHasOrderRetryFailed(true)
        if (!hasTrackedSecondOrder.current) {
          hasTrackedSecondOrder.current = true
          trackSecondOrderIsLoaded({ hasSecondOrderLoaded: false, campaignId })
        }
      }
    }, WAIT_TIME)

    return intervalId
  }, [orderIds.length, userLoadOrders, orders, trackSecondOrderIsLoaded, campaignId])

  useEffect(() => {
    const intervalId = tryFetchOrders()

    return () => {
      clearInterval(intervalId)
    }
  }, [tryFetchOrders])

  useEffect(() => {
    if (secondaryOrder && !hasTrackedSecondOrder.current) {
      hasTrackedSecondOrder.current = true
      trackSecondOrderIsLoaded({ hasSecondOrderLoaded: true, campaignId })
    }
  }, [trackSecondOrderIsLoaded, secondaryOrder, campaignId])

  useEffect(() => {
    const currentOrderCount = Object.keys(orders).length
    if (
      prevOrderCount.current === 1 &&
      currentOrderCount > 1 &&
      pollingAttempts.current <= ORDERS_FETCH_MAX_ATTEMPTS
    ) {
      trackNextOrderIsLoadedWithinPollingLimit({ hasNextOrderLoadedWithinPollingLimit: true })
    }
    prevOrderCount.current = currentOrderCount
  }, [orders, trackNextOrderIsLoadedWithinPollingLimit])

  const handleViewUpcomingBoxes = useCallback(() => {
    trackClickOnViewUpcomingBoxes()

    browserHistory.push(`/my-gousto`)
  }, [trackClickOnViewUpcomingBoxes])

  const handleChooseRecipes = useCallback(() => {
    trackClickOnChooseRecipes()
    browserHistory.push(`/menu/${secondaryOrder?.id}`)
  }, [secondaryOrder, trackClickOnChooseRecipes])

  const daysLeftToChooseRecipes = () => {
    if (!secondaryOrder?.shouldCutoffAt) {
      return 0
    }

    const cutoffDate = moment(secondaryOrder.shouldCutoffAt, 'YYYY-MM-DD HH:mm:ss')
    const today = moment().startOf('day')
    const daysLeft = cutoffDate.startOf('day').diff(today, 'days')

    return Math.max(daysLeft, 0)
  }

  if (hasOrderRetryFailed) {
    return <WhatsComingUpNext />
  }

  if (!secondaryOrder) {
    return (
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        data-testid="loading"
      >
        <Box width="3rem" height="3rem">
          <Loading />
        </Box>
      </Box>
    )
  }

  const percentageOff = parseInt(secondaryOrder.prices.percentageOff, 10)
  const deliveryDate = getFormattedDeliveryDate(secondaryOrder.deliveryDate)
  const hasDiscount = percentageOff > 0

  const renderPill = (labelText: string) => (
    <Box
      borderRadius={1.5}
      display={Display.InlineBlock}
      bg={Color.Success_900}
      paddingH={1}
      paddingV={0.5}
    >
      <Body3 fontWeight={FontWeight.Bold} color={Color.White}>
        {labelText}
      </Body3>
    </Box>
  )

  return (
    <Box>
      <Join with={<Space direction="vertical" size={2} />}>
        <Join with={<Space direction="vertical" size={0.5} />}>
          <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
            <Box display={Display.Flex}>
              <Box paddingRight={2}>
                <IconBackground>
                  <WhiteIcon name="refer_a_friend" />
                </IconBackground>
              </Box>
              <Box>
                <ChooseHeading4>Choose your next box</ChooseHeading4>
                <Text size={1}>Arrives {deliveryDate}</Text>
              </Box>
            </Box>
            <Box>
              <Join with={<Space direction="horizontal" size={1} />}>
                {hasDiscount && renderPill(`${percentageOff}% OFF`)}
              </Join>
            </Box>
          </Box>
        </Join>
        <Space direction="vertical" size={1} />
        <Box display={Display.Flex}>
          <Join with={<Space direction="horizontal" size={1} />}>
            <DaysToOrder>{daysLeftToChooseRecipes()} days left to choose recipes</DaysToOrder>
          </Join>
        </Box>
        <Space direction="vertical" size={1} />
        <Button width="100%" onClick={handleChooseRecipes}>
          Choose recipes
        </Button>
        <Space direction="vertical" size={2} />
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <Heading5 color={Color.ColdGrey_900}>Next box best sellers</Heading5>
          <Space direction="vertical" size={3} />
          <Carousel order={secondaryOrder} />
        </Box>
        <Button
          width="100%"
          onClick={handleViewUpcomingBoxes}
          colorVariant={ButtonColorVariant.Secondary}
          height="48px"
        >
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
          >
            <Box paddingRight={2}>View my upcoming boxes</Box>
            <Icon name="arrow_right" variant={IconVariant.Actionable} />
          </Box>
        </Button>
      </Join>
    </Box>
  )
}
